import getTextColor from '@dashboard/common/utils/getTextColor'
import { editableSectionClasses } from 'features/EmailEditor/constants'
import { getTemplate } from 'features/EmailEditor/templates'
import {
  extractSocialMediaUrls,
  generateSocialMediaLinksHtml,
  getHtmlFromMjml,
  replaceTagsSyntax,
  replaceTagsWithPreviewContent
} from 'features/EmailEditor/utils'
import useBrand from 'hooks/useBrand'
import { marked } from 'marked'
import { createContext, useContext, useEffect, useLayoutEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router'
import campaignApi from 'services/api/campaign'

import styles from './EmailEditor.module.scss'

async function calculateLogoDimensions(logoUrl) {
  return new Promise(resolve => {
    var newImg = new Image()
    newImg.onload = function () {
      var naturalHeight = newImg.height
      var naturalWidth = newImg.width

      const ratio = naturalWidth / naturalHeight
      const height = 60
      const width = height * ratio

      resolve({ width: `${width}px`, height: `${height}px` })
    }

    newImg.src = logoUrl
  })
}

const EmailEditorContext = createContext()

export const useEmailEditor = () => {
  const context = useContext(EmailEditorContext)
  if (!context) {
    throw new Error('useEmailEditor must be used within an EmailEditorProvider')
  }
  return context
}

export const EmailEditorProvider = ({ children }) => {
  const [editingSection, setEditingSection] = useState('inbox-preview')
  const [selectedTemplate, setSelectedTemplate] = useState('default')
  const [hoveredPreview, setHoveredPreview] = useState(null)
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 })
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(true)
  const [emailHtml, setEmailHtml] = useState()
  const [previewHtml, setPreviewHtml] = useState()

  const { id } = useParams()
  const userName = useSelector(s => s.login.currentUser.name)
  const currentUser = useSelector(s => s.login.currentUser)
  const { colors, logoUrl: brandDefaultLogoUrl } = useBrand()

  const [contentIndex, setContentIndex] = useState(0)

  // Get current template and its default parameters
  const currentTemplate = getTemplate(selectedTemplate)
  const [emailState, setEmailState] = useState(currentTemplate.params)

  useEffect(() => {
    setContentIndex(prevIndex => prevIndex + 1)
  }, [emailState])

  const sections = currentTemplate.sections

  // Function to scroll to a section in the preview
  const scrollToSection = sectionClass => {
    const targetElement = document.querySelector(`${sectionClass}`)
    if (targetElement) {
      targetElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest'
      })

      // Add highlight effect
      document.querySelectorAll(`.${styles.highlightedSection}`).forEach(el => {
        el.classList.remove(styles.highlightedSection)
      })
      targetElement.classList.add(styles.highlightedSection)
    }
  }

  // Load campaign email data
  useEffect(() => {
    if (id) {
      campaignApi
        .getCampaignEmail({ campaignId: id })
        .then(emailData => {
          setEmailState(prevState => ({
            ...prevState,
            ...emailData.emailParams,
            ...emailData.globalParams
          }))
        })
        .catch(err => {
          console.error(err)
        })
    }
  }, [id])

  function refreshEmailData() {
    const themeDefault = getTemplate(currentTemplate)

    if (id) {
      campaignApi
        .getCampaignEmail({ campaignId: id })
        .then(emailData => ({
          ...themeDefault.params,
          ...emailData.emailParams,
          ...emailData.globalParams
        }))
        .then(newState => {
          calculateLogoDimensions(newState.logoUrl || brandDefaultLogoUrl).then(
            dimensions => {
              setEmailState(prevState => ({
                ...newState,
                logoWidth: dimensions.width,
                logoHeight: dimensions.height,
                selectedTemplate: prevState.selectedTemplate,
                subject: prevState.subject,
                preheaderText: prevState.preheaderText
              }))
            }
          )
        })
        .catch(err => {
          console.error(err)
        })
    }
  }

  // Logo handling
  useLayoutEffect(() => {
    if (!emailState.logoUrl) return

    calculateLogoDimensions(emailState.logoUrl).then(dimensions => {
      setEmailState(prevState => ({
        ...prevState,
        logoWidth: dimensions.width,
        logoHeight: dimensions.height
      }))
    })
  }, [emailState.logoUrl, selectedTemplate])

  // Brand colors and logo setup
  useEffect(() => {
    if (!emailState.logoUrl) {
      setEmailState(prevState => ({
        ...prevState,
        logoUrl: brandDefaultLogoUrl
      }))
    }

    if (emailState.headerBgColor === 'transparent') {
      setEmailState(prevState => ({
        ...prevState,
        headerBgColor: colors.primary
      }))
    }

    if (emailState.ctaBackgroundColor === 'transparent') {
      setEmailState(prevState => ({
        ...prevState,
        ctaBackgroundColor: colors.primary
      }))
    }
  }, [colors, brandDefaultLogoUrl, emailState])

  // CTA text color update
  useEffect(() => {
    const newCtaTextColor = getTextColor(emailState.ctaBackgroundColor)
    if (newCtaTextColor !== emailState.ctaTextColor) {
      updateEmailState('ctaTextColor', newCtaTextColor)
    }
  }, [emailState.ctaBackgroundColor, emailState.ctaTextColor])

  // Generate email HTML
  useEffect(() => {
    const socialUrls = extractSocialMediaUrls(emailState.socialMediaLinks)

    let html = getHtmlFromMjml(
      currentTemplate.template,
      {
        ...emailState,
        ...socialUrls
      },
      {
        ...Object.keys(emailState?.richtextFields).reduce((acc, key) => {
          acc[key] = emailState.richtextFields[key]
            ? marked.parse(replaceTagsSyntax(emailState.richtextFields[key]))
            : ''
          return acc
        }, {}),
        socialMediaLinksHtml: generateSocialMediaLinksHtml(emailState.socialMediaLinks)
      }
    )

    if (editingSection) {
      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = html
      const sectionElement = tempDiv.querySelector(`.mjml-${editingSection}`)
      sectionElement && sectionElement.classList.add(styles.highlightedSection)
      setPreviewHtml(replaceTagsWithPreviewContent(tempDiv.innerHTML, userName))
    } else {
      setPreviewHtml(replaceTagsWithPreviewContent(html, userName))
    }

    setEmailHtml(html)
  }, [emailState, editingSection, userName, currentTemplate.template])

  // State update functions
  const updateEmailState = (property, value) => {
    setEmailState(prevState => ({
      ...prevState,
      [property]: value
    }))
    setHasUnsavedChanges(true)
  }

  const updateRichTextField = (field, value) => {
    setEmailState(prevState => ({
      ...prevState,
      richtextFields: {
        ...prevState.richtextFields,
        [field]: value
      }
    }))
  }

  // Handle template change
  const handleTemplateChange = templateName => {
    setSelectedTemplate(templateName)
    const newTemplate = getTemplate(templateName)

    setEmailState(prevState => {
      return {
        ...newTemplate.params,
        ...prevState,
        selectedTemplate: templateName,
        richtextFields: {
          ...newTemplate.params.richtextFields,
          ...prevState.richtextFields
        }
      }
    })
  }

  useEffect(() => {
    handleTemplateChange(emailState.selectedTemplate || 'default')
  }, [emailState.selectedTemplate])

  // Handle social media links
  const handleSocialMediaChange = (index, field, value) => {
    const updatedLinks = [...emailState.socialMediaLinks]
    updatedLinks[index][field] = value
    updateEmailState('socialMediaLinks', updatedLinks)
  }

  // Handle click on email preview
  const handleClick = e => {
    if (e.target.tagName === 'A' || e.target.closest('a')) {
      e.preventDefault()
    }

    let target = e.target.closest(editableSectionClasses.map(c => `.${c}`).join(','))

    const targetName = [...target.classList].find(c => c.startsWith('mjml-'))

    scrollToSection(`.${targetName}`)

    document.querySelectorAll(`.${styles.highlightedSection}`).forEach(el => {
      el.classList.remove(styles.highlightedSection)
    })
    target &&
      !target.classList.contains(styles.highlightedSection) &&
      target.classList.add(styles.highlightedSection)

    if (target) {
      setEditingSection(
        [...target.classList].find(c => c.startsWith('mjml-')).replace('mjml-', '')
      )
      return
    }
  }

  // Save changes function
  const saveChanges = () => {
    // eslint-disable-next-line no-unused-vars
    const {
      termsUrl,
      privacyPolicyUrl,
      hotelName,
      hotelAddress,
      socialMediaLinks,
      ...emailParams
    } = emailState

    return campaignApi
      .updateCampaignEmail({
        campaignId: id,
        emailParams,
        html: emailHtml,
        globalParams: { socialMediaLinks }
      })
      .then(() => {
        setHasUnsavedChanges(false)
      })
  }

  // Send test email function
  const sendTestEmail = (email = currentUser.email) => {
    return campaignApi.sendTestEmail({
      contentId: id,
      email,
      emailBodyHtml: emailHtml,
      emailSubject: emailState.subject
    })
  }

  const value = {
    // State
    editingSection,
    setEditingSection,
    selectedTemplate,
    setSelectedTemplate: handleTemplateChange,
    hoveredPreview,
    setHoveredPreview,
    mousePosition,
    setMousePosition,
    emailState,
    setEmailState,
    currentTemplate,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    emailHtml,
    previewHtml,
    currentUser,
    id,
    sections,
    // Functions
    updateEmailState,
    updateRichTextField,
    handleTemplateChange,
    handleSocialMediaChange,
    handleClick,
    saveChanges,
    sendTestEmail,
    refreshEmailData,
    contentIndex
  }

  return (
    <EmailEditorContext.Provider value={value}>{children}</EmailEditorContext.Provider>
  )
}
